import { ChevronDown } from 'lucide-react';
import { TableCell, TableRow } from '@repo/ui/components/table';
import { cn } from '@repo/ui/lib/utils';
import { formatPercent } from '@/lib/format/percent/percent';

import type { InvestorHolding } from '../types/api';
import type { HoldingsValuation } from '../utils/holding-format';
import {
  idOf,
  usdLabel,
  usdOf,
  type LedgerGroup
} from '../utils/owned-positions';
import { LEDGER_ROW_CLASS } from '@/components/lens-panel';
import {
  HoldingRow,
  type LedgerRowContext
} from './owned-positions-holding-row';

/**
 * Rows shown before the rest folds into a "Show N more" tail — the ledger
 * stays scannable at any holding count while nothing becomes unreachable.
 */
const VISIBLE_ROWS = 5;

/** Top rows stay visible; the tail folds behind one reveal control. */
export function MemberList({
  members,
  indent,
  revealed,
  onToggleTail,
  context
}: {
  members: InvestorHolding[];
  indent: boolean;
  revealed: boolean;
  onToggleTail: () => void;
  context: LedgerRowContext;
}) {
  const visible = revealed ? members : members.slice(0, VISIBLE_ROWS);
  const hidden = members.slice(VISIBLE_ROWS);
  // Dollars only: rows with no rate are not added in one for one.
  const hiddenUsd = hidden
    .map((holding) => usdOf(context.facts, holding))
    .filter((usd): usd is number => usd != null);
  const hiddenValue = hiddenUsd.reduce((sum, usd) => sum + usd, 0);

  return (
    <>
      {visible.map((holding) => (
        <HoldingRow
          key={idOf(context.facts, holding)}
          holding={holding}
          indent={indent}
          context={context}
        />
      ))}
      {hidden.length > 0 ? (
        <TableRow className="hover:bg-transparent">
          <TableCell
            colSpan={4}
            className={cn('py-2', indent ? 'pl-11' : 'pl-4')}
          >
            <button
              type="button"
              onClick={onToggleTail}
              className="text-rowa-label-sm text-rowa-text-brand inline-flex items-center gap-1 font-medium hover:opacity-80"
            >
              {revealed
                ? 'Show less'
                : hiddenUsd.length > 0
                  ? `Show ${hidden.length} more · ${usdLabel(hiddenValue)} combined`
                  : `Show ${hidden.length} more`}
              <ChevronDown
                aria-hidden
                className={cn(
                  'size-3.5 transition-transform',
                  revealed && 'rotate-180'
                )}
              />
            </button>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

/** The ledger's closing row: the total, or what the listed rows add up to. */
export function LedgerTotalRow({
  isPartial,
  rowCount,
  valuation,
  totalLabel,
  listed
}: {
  /** The wallet holds more than the rows on this card. */
  isPartial: boolean;
  rowCount: number;
  valuation: HoldingsValuation | null;
  totalLabel: string;
  /** The listed rows' dollar value and share of the stated total. */
  listed: { usd: number; share: number };
}) {
  return (
    <TableRow className="bg-rowa-surface-accent/60 hover:bg-rowa-surface-accent/60">
      <TableCell className="text-rowa-label-sm text-rowa-text-brand py-2.5 pl-4 tracking-[0.06em] uppercase">
        {/* "Reconciled" and "100%" are claims: every priced holding is in
            the total at its dollar value. They go the moment a row is left
            out for lack of a rate. */}
        {isPartial
          ? `These ${rowCount} holdings`
          : valuation?.total.reconciled
            ? 'Total · reconciled'
            : 'Total in dollars'}
      </TableCell>
      <TableCell
        colSpan={3}
        className="text-rowa-label-sm text-rowa-text-primary pr-4 text-right font-mono tabular-nums"
      >
        {isPartial
          ? valuation?.shareBasis === 'page'
            ? usdLabel(listed.usd)
            : `${usdLabel(listed.usd)} · ${formatPercent(listed.share, { digits: 1 })}`
          : valuation?.total.reconciled
            ? `${totalLabel} · 100.0%`
            : totalLabel}
      </TableCell>
    </TableRow>
  );
}

/** A lens bucket's own row: its value, its share, and the toggle. */
export function GroupRow({
  group,
  open,
  segmentId,
  onToggle,
  context
}: {
  group: LedgerGroup;
  open: boolean;
  segmentId: string | null;
  onToggle: () => void;
  context: LedgerRowContext;
}) {
  return (
    <TableRow
      className={cn(LEDGER_ROW_CLASS, 'group')}
      {...(segmentId ? context.rowProps(segmentId) : {})}
    >
      <TableCell className="min-w-52 py-3 pl-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full"
            style={{
              backgroundColor: context.dotColor(segmentId)
            }}
          />
          <div className="min-w-0">
            <p className="text-rowa-body-sm text-rowa-text-primary truncate font-semibold">
              {group.label}
            </p>
            <p className="text-rowa-label-sm text-rowa-text-secondary truncate">
              {group.members.length} holding
              {group.members.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-rowa-body-sm text-rowa-text-primary text-right font-mono tabular-nums">
        {group.valuedCount > 0 ? (
          usdLabel(group.value)
        ) : (
          <span className="text-rowa-text-tertiary">—</span>
        )}
      </TableCell>
      <TableCell className="text-rowa-body-sm text-rowa-text-primary text-right font-mono tabular-nums">
        {/* A bucket with no dollar value has no share of a dollar total
            either. */}
        {group.valuedCount > 0 ? (
          formatPercent(group.share, { digits: 1 })
        ) : (
          <span className="text-rowa-text-tertiary">—</span>
        )}
      </TableCell>
      <TableCell className="pr-4">
        <div className="flex items-center justify-end">
          <button
            type="button"
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} holdings in ${group.label}`}
            onClick={onToggle}
            className="text-rowa-text-tertiary hover:text-rowa-text-primary focus-visible:ring-rowa-border-accent rounded-rowa-full p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            <ChevronDown
              aria-hidden
              className={cn(
                'size-4 transition-transform',
                open && 'rotate-180'
              )}
            />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}
